import React from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { PercentSlider } from '@Components/PercentSlider';
import { useGlobalization } from '@Translations/useGlobalization';
import { LabelInfoTip } from './LabelInfoTip';
import { formatScore } from './workedExampleMaths';

export interface WeightingRowProps {
	lofValue: number;
	cofValue: number;
	/** Weighted LoF / CoF contribution, e.g. 5.0 of a 10-point scale. */
	lofScore: number;
	cofScore: number;
	maxLoFValue: number;
	maxCoFValue: number;
	maxScore: number;
	fullWeight: boolean;
	disabled: boolean;
	onLofChange: (value: number) => void;
	onCofChange: (value: number) => void;
	onFullWeightChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const showNumber = (value: number): string =>
	Number.isNaN(value) ? '–' : String(value);

/** "5.0 / 10"; never prints NaN. */
export const axisScoreText = (score: number, max: number): string =>
	`${formatScore(score) || '–'} / ${showNumber(max)}`;

const SliderRow = ({
	label,
	value,
	scoreText,
	dataCy,
	disabled,
	invalid,
	onChange,
}: {
	label: string;
	value: number;
	scoreText: string;
	dataCy: string;
	disabled: boolean;
	invalid: boolean;
	onChange: (v: number) => void;
}) => (
	<Box
		sx={{
			display: 'flex',
			alignItems: 'center',
			gap: 2,
			py: 1.5,
			borderTop: '1px solid #f1f3f6',
		}}>
		<Typography
			sx={{ fontSize: 14, color: '#1b2733', width: 190, flex: 'none' }}>
			{label}
		</Typography>
		<Box sx={{ flex: 1, minWidth: 0 }}>
			<PercentSlider
				redesigned
				tone="primary"
				accent="#3b8fd4"
				dataCy={dataCy}
				value={value}
				disabled={disabled}
				invalid={invalid}
				onSliderChanged={onChange}
			/>
		</Box>
		<Typography
			data-cy={`${dataCy}-score`}
			sx={{
				fontSize: 13,
				color: '#5b6472',
				width: 70,
				flex: 'none',
				textAlign: 'right',
			}}>
			{scoreText}
		</Typography>
	</Box>
);

export const WeightingRow = ({
	lofValue,
	cofValue,
	lofScore,
	cofScore,
	maxLoFValue,
	maxCoFValue,
	maxScore,
	fullWeight,
	disabled,
	onLofChange,
	onCofChange,
	onFullWeightChange,
}: WeightingRowProps) => {
	const { t } = useGlobalization();
	return (
		<Box>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					flexWrap: 'wrap',
					gap: 1,
					mb: 0.5,
				}}>
				<Typography
					sx={{ fontSize: 15, fontWeight: 700, color: '#0d1520' }}>
					{t('LoF vs CoF weighting')}
				</Typography>
				<FormControlLabel
					disabled={disabled}
					control={
						<Checkbox
							size="small"
							checked={fullWeight}
							onChange={onFullWeightChange}
						/>
					}
					label={
						<Box
							component="span"
							sx={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 0.5,
								fontSize: 14,
							}}>
							{t('Use full weight for both LoF and CoF')}
							<LabelInfoTip
								dataCy="full-weight-help"
								text={t(
									'Score every pipe on LoF and CoF at full weight (100% each) instead of splitting 100% between them — useful to see each axis on its own.',
								)}
							/>
						</Box>
					}
				/>
			</Box>
			<Typography sx={{ fontSize: 13, color: '#5b6472', mb: 0.5 }}>
				{t(
					'Set how much LoF and CoF contribute to the overall risk score. The default is 50/50.',
				)}
			</Typography>
			<SliderRow
				label={t('Likelihood of Failure')}
				value={lofValue}
				scoreText={axisScoreText(lofScore, maxLoFValue)}
				dataCy="overall-risk-0"
				disabled={disabled || fullWeight}
				invalid={maxScore === 0}
				onChange={onLofChange}
			/>
			<SliderRow
				label={t('Consequence of Failure')}
				value={cofValue}
				scoreText={axisScoreText(cofScore, maxCoFValue)}
				dataCy="overall-risk-1"
				disabled={disabled || fullWeight}
				invalid={maxScore === 0}
				onChange={onCofChange}
			/>
			<Typography
				data-cy="max-overall-risk-score"
				sx={{
					fontSize: 13,
					fontWeight: 700,
					color: '#1b2733',
					textAlign: 'right',
					mt: 1.5,
				}}>
				{t('Max Overall Risk Score = {{score}}', {
					score: showNumber(maxScore),
				})}
			</Typography>
		</Box>
	);
};
